/**
 * ESRS S3 — Affected Communities.
 * Community registry, impact assessments (severity × likelihood), FPIC tracking
 * for indigenous communities, and grievance channels (S3-3). Chrome and tokens
 * come exclusively from EsgKit; endpoints under /api/esrs/s3 (esrs_s3_routes.py).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Users, Feather, ShieldCheck, ShieldQuestion, ShieldAlert, ShieldX, Shield,
  TrendingDown, TrendingUp, Plus, AlertTriangle, MessageSquare, MapPin,
} from "lucide-react";
import {
  Page, PageHeader, Card, Overline, Metric, Badge, Data, Btn, TabBar,
  DataTable, EmptyState, ErrorBanner, Skeleton, field, Label,
  apiFetch, fmt, C,
} from "../../../components/esg/EsgKit";
import { useEntities } from "../../../hooks/useEntities";

// ─── Enum → Badge tone maps (verbatim from models/esrs_social.py) ────────────
const FPIC_META = {
  not_started: { c: C.neutral, Icon: Shield, label: "Not started" },
  in_consultation: { c: C.accent, Icon: ShieldQuestion, label: "In consultation" },
  consent_given: { c: C.primary, Icon: ShieldCheck, label: "Consent given" },
  consent_withheld: { c: C.destructive, Icon: ShieldX, label: "Consent withheld" },
  consent_withdrawn: { c: C.destructive, Icon: ShieldAlert, label: "Consent withdrawn" },
};
const FPIC_OPTIONS = [
  "not_started", "in_consultation", "consent_given",
  "consent_withheld", "consent_withdrawn",
];

const SEVERITY_META = {
  critical: C.destructive, high: C.destructive,
  medium: C.accent, low: C.neutral, negligible: C.neutral,
};
const SEVERITY_OPTIONS = ["critical", "high", "medium", "low", "negligible"];
const LIKELIHOOD_OPTIONS = ["almost_certain", "likely", "possible", "unlikely", "rare"];

const GRIEVANCE_META = {
  identified: { c: C.neutral, label: "Identified" },
  planned: { c: C.neutral, label: "Planned" },
  in_progress: { c: C.accent, label: "In progress" },
  completed: { c: C.primary, label: "Completed" },
  verified: { c: C.primary, label: "Verified" },
  closed: { c: C.neutral, label: "Closed" },
  ineffective: { c: C.destructive, label: "Ineffective" },
};
const LIFECYCLE_OPTIONS = [
  "identified", "planned", "in_progress", "completed",
  "verified", "closed", "ineffective",
];
const CHANNEL_OPTIONS = [
  "community_meeting", "hotline", "written_submission",
  "ombudsperson", "ngo_intermediary", "other",
];

const label = (s) =>
  !s ? "—" : String(s).replace(/_/g, " ").replace(/^\w/, (m) => m.toUpperCase());
const truncate = (s, n = 60) =>
  !s ? "—" : s.length > n ? `${s.slice(0, n)}…` : s;

// ─── Small presentational helpers built only from kit primitives ─────────────
const Kpi = ({ label: l, value, unit, color }) => (
  <Card style={{ padding: 16 }}>
    <Metric label={l} value={value} unit={unit} color={color} />
  </Card>
);

const FieldRow = ({ children, cols = "1fr 1fr" }) => (
  <div style={{ display: "grid", gridTemplateColumns: cols, gap: 12, marginBottom: 12 }}>
    {children}
  </div>
);

const CheckField = ({ children, checked, onChange }) => (
  <label style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "inherit", fontSize: 13, color: C.body, cursor: "pointer" }}>
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ accentColor: C.primary, width: 15, height: 15 }} />
    {children}
  </label>
);

export default function S3Communities() {
  const { entities, entityId, setEntityId } = useEntities("esrs_s3_entity_id");

  const [summary, setSummary] = useState(null);
  const [communities, setCommunities] = useState([]);
  const [impacts, setImpacts] = useState([]);
  const [grievances, setGrievances] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("communities");

  // Server-side filters
  const [impactSeverity, setImpactSeverity] = useState("");
  const [impactType, setImpactType] = useState("");
  const [grievanceStatus, setGrievanceStatus] = useState("");

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    setError("");
    try {
      const qImp = new URLSearchParams({ entity_id: entityId });
      if (impactSeverity) qImp.set("severity", impactSeverity);
      if (impactType) qImp.set("impact_type", impactType);
      const qGrv = new URLSearchParams({ entity_id: entityId });
      if (grievanceStatus) qGrv.set("status", grievanceStatus);

      const [s, c, i, g] = await Promise.all([
        apiFetch(`/api/esrs/s3/summary?entity_id=${encodeURIComponent(entityId)}`),
        apiFetch(`/api/esrs/s3/communities?entity_id=${encodeURIComponent(entityId)}`),
        apiFetch(`/api/esrs/s3/impacts?${qImp.toString()}`),
        apiFetch(`/api/esrs/s3/grievances?${qGrv.toString()}`),
      ]);
      setSummary(s);
      setCommunities(c?.communities || []);
      setImpacts(i?.impacts || []);
      setGrievances(g?.grievances || []);
    } catch (e) {
      setError(e.message || "Failed to load ESRS S3 data");
    } finally {
      setLoading(false);
    }
  }, [entityId, impactSeverity, impactType, grievanceStatus]);

  useEffect(() => { load(); }, [load]);

  const communityName = useMemo(() => {
    const m = {};
    communities.forEach((c) => { m[c.community_id] = c.name; });
    return (id) => m[id] || id;
  }, [communities]);

  const tabs = [
    { id: "communities", label: "Communities", count: communities.length },
    { id: "impacts", label: "Impacts", count: impacts.length },
    { id: "grievances", label: "Grievances", count: grievances.length },
  ];

  return (
    <Page>
      <PageHeader
        overline="ESRS S3 · Affected Communities"
        OverlineIcon={Users}
        title="Affected Communities"
        subtitle="Community registry, impact assessments (severity × likelihood), free, prior and informed consent (FPIC) tracking for indigenous communities, and grievance channels for raising concerns."
        entities={entities}
        entityId={entityId}
        onEntityChange={setEntityId}
        onRefresh={load}
        loading={loading}
      />

      <ErrorBanner message={error} onDismiss={() => setError("")} />

      {!entityId ? (
        <EmptyState
          Icon={Users}
          title="No reporting entity selected"
          body="Select a reporting entity to view its affected-communities registry, impact assessments, and grievance records."
        />
      ) : loading && !summary ? (
        <div style={{ display: "grid", gap: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
            <Skeleton h={72} /><Skeleton h={72} /><Skeleton h={72} /><Skeleton h={72} />
          </div>
          <Skeleton h={320} />
        </div>
      ) : (
        <>
          <KpiStrip summary={summary} />

          <div style={{ marginTop: 20 }}>
            <TabBar tabs={tabs} active={tab} onChange={setTab} />

            {tab === "communities" && (
              <CommunitiesTab
                entityId={entityId}
                communities={communities}
                onMutate={load}
                onError={setError}
              />
            )}
            {tab === "impacts" && (
              <ImpactsTab
                entityId={entityId}
                impacts={impacts}
                communities={communities}
                communityName={communityName}
                severity={impactSeverity}
                setSeverity={setImpactSeverity}
                impactType={impactType}
                setImpactType={setImpactType}
                onMutate={load}
                onError={setError}
              />
            )}
            {tab === "grievances" && (
              <GrievancesTab
                entityId={entityId}
                grievances={grievances}
                communities={communities}
                communityName={communityName}
                statusFilter={grievanceStatus}
                setStatusFilter={setGrievanceStatus}
                onMutate={load}
                onError={setError}
              />
            )}
          </div>
        </>
      )}
    </Page>
  );
}

// ─── KPI strip (GET /summary) ────────────────────────────────────────────────
function KpiStrip({ summary }) {
  const comm = summary?.communities || {};
  const grv = summary?.grievances || {};
  const imp = summary?.impacts || {};
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
      <Kpi label="Communities" value={fmt(comm.total, 0)} />
      <Kpi label="Indigenous communities" value={fmt(comm.indigenous, 0)} color={C.locked} />
      <Kpi label="Open grievances" value={fmt(grv.open, 0)} color={grv.open ? C.accent : C.ink} />
      <Kpi label="Material negative impacts" value={fmt(imp.negative_material, 0)} color={imp.negative_material ? C.destructive : C.ink} />
    </div>
  );
}

// ─── Communities tab ─────────────────────────────────────────────────────────
function CommunitiesTab({ entityId, communities, onMutate, onError }) {
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", location: "", relation_to_operations: "", site_ref: "", indigenous: false,
  });
  // Which indigenous row has its FPIC editor open + the pending value.
  const [fpicOpen, setFpicOpen] = useState(null);
  const [fpicValue, setFpicValue] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { onError("Community name is required"); return; }
    setBusy(true);
    try {
      await apiFetch("/api/esrs/s3/communities", {
        method: "POST",
        body: { entity_id: entityId, ...form, name: form.name.trim() },
      });
      setForm({ name: "", location: "", relation_to_operations: "", site_ref: "", indigenous: false });
      setShowForm(false);
      onMutate();
    } catch (err) {
      onError(err.message || "Failed to register community");
    } finally {
      setBusy(false);
    }
  };

  const saveFpic = async (id) => {
    setBusy(true);
    try {
      await apiFetch(`/api/esrs/s3/communities/${id}/fpic`, {
        method: "POST",
        body: { status: fpicValue },
      });
      setFpicOpen(null);
      onMutate();
    } catch (err) {
      onError(err.message || "Failed to update FPIC status");
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    { key: "name", label: "Community", render: (r) => <Data color={C.ink}>{r.name}</Data> },
    { key: "location", label: "Location", render: (r) => <Data color={C.body}>{r.location || "—"}</Data> },
    {
      key: "relation_to_operations", label: "Relation to operations",
      render: (r) => <span style={{ fontSize: 12, color: C.body }}>{r.relation_to_operations || "—"}</span>,
    },
    {
      key: "indigenous", label: "Indigenous",
      render: (r) => (r.indigenous
        ? <Badge c={C.locked} Icon={Feather} label="Indigenous" />
        : <Data color={C.muted}>—</Data>),
    },
    {
      key: "fpic_status", label: "FPIC",
      render: (r) => {
        if (!r.indigenous) return <Data color={C.muted}>n/a</Data>;
        const meta = FPIC_META[r.fpic_status] || FPIC_META.not_started;
        return <Badge c={meta.c} Icon={meta.Icon} label={meta.label} />;
      },
    },
    {
      key: "_action", label: "", align: "right",
      render: (r) => {
        if (!r.indigenous) return null;
        if (fpicOpen === r.community_id) {
          return (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <select
                value={fpicValue}
                onChange={(e) => setFpicValue(e.target.value)}
                aria-label="FPIC status"
                style={{ ...field, width: "auto", padding: "5px 8px", fontSize: 12 }}
              >
                {FPIC_OPTIONS.map((o) => (
                  <option key={o} value={o}>{FPIC_META[o].label}</option>
                ))}
              </select>
              <Btn variant="primary" onClick={() => saveFpic(r.community_id)} disabled={busy}>Save</Btn>
              <Btn variant="ghost" onClick={() => setFpicOpen(null)}>Cancel</Btn>
            </div>
          );
        }
        return (
          <Btn
            variant="secondary"
            onClick={() => { setFpicOpen(r.community_id); setFpicValue(r.fpic_status || "not_started"); }}
          >
            FPIC
          </Btn>
        );
      },
    },
  ];

  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <Overline Icon={Users} style={{ marginBottom: 0 }}>Community register</Overline>
        <Btn variant="secondary" Icon={Plus} onClick={() => setShowForm((s) => !s)}>
          {showForm ? "Close" : "Register community"}
        </Btn>
      </div>

      {showForm && (
        <form onSubmit={submit} style={{ padding: 16, marginBottom: 16, border: `1px solid ${C.border}`, borderRadius: 8, background: C.bg }}>
          <FieldRow>
            <div>
              <Label>Name *</Label>
              <input style={field} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div>
              <Label>Location</Label>
              <input style={field} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </div>
          </FieldRow>
          <FieldRow>
            <div>
              <Label>Relation to operations</Label>
              <input style={field} value={form.relation_to_operations} onChange={(e) => setForm({ ...form, relation_to_operations: e.target.value })} />
            </div>
            <div>
              <Label>Site ref</Label>
              <input style={field} value={form.site_ref} onChange={(e) => setForm({ ...form, site_ref: e.target.value })} />
            </div>
          </FieldRow>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
            <CheckField checked={form.indigenous} onChange={(v) => setForm({ ...form, indigenous: v })}>
              Indigenous community (enables FPIC tracking)
            </CheckField>
            <Btn type="submit" variant="primary" Icon={Plus} disabled={busy}>
              {busy ? "Saving…" : "Register"}
            </Btn>
          </div>
        </form>
      )}

      {communities.length === 0 ? (
        <EmptyState
          Icon={MapPin}
          title="No communities registered"
          body="Register affected communities near your operations to track impacts, FPIC status, and grievances."
        />
      ) : (
        <DataTable columns={columns} rows={communities} keyField="community_id" emptyLabel="No communities" />
      )}
    </Card>
  );
}

// ─── Impacts tab ─────────────────────────────────────────────────────────────
function ImpactsTab({
  entityId, impacts, communities, communityName,
  severity, setSeverity, impactType, setImpactType, onMutate, onError,
}) {
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    community_id: "", impact_type: "negative", severity: "medium",
    likelihood: "possible", affected_people_estimate: "", is_material: false, description: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.community_id) { onError("Select a community for the impact"); return; }
    setBusy(true);
    try {
      await apiFetch("/api/esrs/s3/impacts", {
        method: "POST",
        body: {
          entity_id: entityId,
          community_id: form.community_id,
          impact_type: form.impact_type,
          severity: form.severity,
          likelihood: form.likelihood,
          affected_people_estimate: Number(form.affected_people_estimate) || 0,
          is_material: form.is_material,
          description: form.description,
        },
      });
      setForm({
        community_id: "", impact_type: "negative", severity: "medium",
        likelihood: "possible", affected_people_estimate: "", is_material: false, description: "",
      });
      setShowForm(false);
      onMutate();
    } catch (err) {
      onError(err.message || "Failed to record impact");
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: "community_id", label: "Community",
      render: (r) => <Data color={C.ink}>{communityName(r.community_id)}</Data>,
    },
    {
      key: "impact_type", label: "Type",
      render: (r) => (r.impact_type === "negative"
        ? <Badge c={C.destructive} Icon={TrendingDown} label="Negative" />
        : <Badge c={C.primary} Icon={TrendingUp} label="Positive" />),
    },
    {
      key: "severity", label: "Severity",
      render: (r) => <Badge c={SEVERITY_META[r.severity] || C.neutral} Icon={AlertTriangle} label={label(r.severity)} />,
    },
    {
      key: "likelihood", label: "Likelihood",
      render: (r) => <Data color={C.body}>{label(r.likelihood)}</Data>,
    },
    {
      key: "affected_people_estimate", label: "Affected people", align: "right",
      render: (r) => <Data color={C.body}>{fmt(r.affected_people_estimate, 0)}</Data>,
    },
    {
      key: "is_material", label: "Material",
      render: (r) => (r.is_material ? <Badge c={C.accent} Icon={AlertTriangle} label="Material" /> : <Data color={C.muted}>—</Data>),
    },
    {
      key: "description", label: "Description",
      render: (r) => <span style={{ fontSize: 12, color: C.body }} title={r.description || ""}>{truncate(r.description)}</span>,
    },
  ];

  const filterSelect = { ...field, width: "auto", minWidth: 150, padding: "6px 10px" };

  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, gap: 12, flexWrap: "wrap" }}>
        <Overline Icon={AlertTriangle} style={{ marginBottom: 0 }}>Impact assessments</Overline>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <select value={severity} onChange={(e) => setSeverity(e.target.value)} aria-label="Filter by severity" style={filterSelect}>
            <option value="">All severities</option>
            {SEVERITY_OPTIONS.map((o) => <option key={o} value={o}>{label(o)}</option>)}
          </select>
          <select value={impactType} onChange={(e) => setImpactType(e.target.value)} aria-label="Filter by impact type" style={filterSelect}>
            <option value="">All types</option>
            <option value="negative">Negative</option>
            <option value="positive">Positive</option>
          </select>
          <Btn variant="secondary" Icon={Plus} onClick={() => setShowForm((s) => !s)}>
            {showForm ? "Close" : "Record impact"}
          </Btn>
        </div>
      </div>

      {showForm && (
        <form onSubmit={submit} style={{ padding: 16, marginBottom: 16, border: `1px solid ${C.border}`, borderRadius: 8, background: C.bg }}>
          <FieldRow>
            <div>
              <Label>Community *</Label>
              <select style={field} value={form.community_id} onChange={(e) => setForm({ ...form, community_id: e.target.value })} required>
                <option value="" disabled>Select community…</option>
                {communities.map((c) => <option key={c.community_id} value={c.community_id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Impact type *</Label>
              <select style={field} value={form.impact_type} onChange={(e) => setForm({ ...form, impact_type: e.target.value })}>
                <option value="negative">Negative</option>
                <option value="positive">Positive</option>
              </select>
            </div>
          </FieldRow>
          <FieldRow cols="1fr 1fr 1fr">
            <div>
              <Label>Severity *</Label>
              <select style={field} value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>
                {SEVERITY_OPTIONS.map((o) => <option key={o} value={o}>{label(o)}</option>)}
              </select>
            </div>
            <div>
              <Label>Likelihood *</Label>
              <select style={field} value={form.likelihood} onChange={(e) => setForm({ ...form, likelihood: e.target.value })}>
                {LIKELIHOOD_OPTIONS.map((o) => <option key={o} value={o}>{label(o)}</option>)}
              </select>
            </div>
            <div>
              <Label>Affected people (est.)</Label>
              <input type="number" min="0" style={field} value={form.affected_people_estimate} onChange={(e) => setForm({ ...form, affected_people_estimate: e.target.value })} />
            </div>
          </FieldRow>
          <div style={{ marginBottom: 12 }}>
            <Label>Description</Label>
            <textarea rows={3} style={{ ...field, resize: "vertical" }} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <CheckField checked={form.is_material} onChange={(v) => setForm({ ...form, is_material: v })}>
              Material impact
            </CheckField>
            <Btn type="submit" variant="primary" Icon={Plus} disabled={busy}>
              {busy ? "Saving…" : "Record"}
            </Btn>
          </div>
        </form>
      )}

      {impacts.length === 0 ? (
        <EmptyState
          Icon={AlertTriangle}
          title="No impact assessments"
          body="Record positive and negative impacts on affected communities, scored by severity and likelihood, to support S3-4 disclosure."
        />
      ) : (
        <DataTable columns={columns} rows={impacts} keyField="assessment_id" emptyLabel="No impacts" />
      )}
    </Card>
  );
}

// ─── Grievances tab ──────────────────────────────────────────────────────────
function GrievancesTab({
  entityId, grievances, communities, communityName,
  statusFilter, setStatusFilter, onMutate, onError,
}) {
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    community_id: "", channel: "community_meeting", received_date: "", description: "",
  });
  // Inline status editor state.
  const [statusOpen, setStatusOpen] = useState(null);
  const [statusValue, setStatusValue] = useState("");
  const [resolution, setResolution] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.community_id) { onError("Select a community for the grievance"); return; }
    if (!form.received_date) { onError("Received date is required"); return; }
    setBusy(true);
    try {
      await apiFetch("/api/esrs/s3/grievances", {
        method: "POST",
        body: {
          entity_id: entityId,
          community_id: form.community_id,
          channel: form.channel,
          received_date: form.received_date,
          description: form.description,
        },
      });
      setForm({ community_id: "", channel: "community_meeting", received_date: "", description: "" });
      setShowForm(false);
      onMutate();
    } catch (err) {
      onError(err.message || "Failed to record grievance");
    } finally {
      setBusy(false);
    }
  };

  const saveStatus = async (id) => {
    setBusy(true);
    try {
      await apiFetch(`/api/esrs/s3/grievances/${id}/status`, {
        method: "POST",
        body: {
          status: statusValue,
          ...(resolution.trim() ? { resolved_date: resolution.trim() } : {}),
        },
      });
      setStatusOpen(null);
      setResolution("");
      onMutate();
    } catch (err) {
      onError(err.message || "Failed to update grievance status");
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: "community_id", label: "Community",
      render: (r) => <Data color={C.ink}>{communityName(r.community_id)}</Data>,
    },
    { key: "channel", label: "Channel", render: (r) => <Data color={C.body}>{label(r.channel)}</Data> },
    { key: "received_date", label: "Received", render: (r) => <Data color={C.body}>{r.received_date || "—"}</Data> },
    {
      key: "status", label: "Status",
      render: (r) => {
        const meta = GRIEVANCE_META[r.status] || { c: C.neutral, label: label(r.status) };
        return <Badge c={meta.c} Icon={MessageSquare} label={meta.label} />;
      },
    },
    {
      key: "description", label: "Description",
      render: (r) => <span style={{ fontSize: 12, color: C.body }} title={r.description || ""}>{truncate(r.description)}</span>,
    },
    {
      key: "_action", label: "", align: "right",
      render: (r) => {
        if (statusOpen === r.grievance_id) {
          return (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <select
                value={statusValue}
                onChange={(e) => setStatusValue(e.target.value)}
                aria-label="Grievance status"
                style={{ ...field, width: "auto", padding: "5px 8px", fontSize: 12 }}
              >
                {LIFECYCLE_OPTIONS.map((o) => (
                  <option key={o} value={o}>{(GRIEVANCE_META[o] || {}).label || label(o)}</option>
                ))}
              </select>
              <input
                type="date"
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                aria-label="Resolved date"
                style={{ ...field, width: "auto", padding: "5px 8px", fontSize: 12 }}
              />
              <Btn variant="primary" onClick={() => saveStatus(r.grievance_id)} disabled={busy}>Save</Btn>
              <Btn variant="ghost" onClick={() => { setStatusOpen(null); setResolution(""); }}>Cancel</Btn>
            </div>
          );
        }
        return (
          <Btn
            variant="secondary"
            onClick={() => { setStatusOpen(r.grievance_id); setStatusValue(r.status); setResolution(r.resolved_date || ""); }}
          >
            Update status
          </Btn>
        );
      },
    },
  ];

  const filterSelect = { ...field, width: "auto", minWidth: 150, padding: "6px 10px" };

  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, gap: 12, flexWrap: "wrap" }}>
        <Overline Icon={MessageSquare} style={{ marginBottom: 0 }}>Grievance channels</Overline>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Filter by status" style={filterSelect}>
            <option value="">All statuses</option>
            {LIFECYCLE_OPTIONS.map((o) => <option key={o} value={o}>{(GRIEVANCE_META[o] || {}).label || label(o)}</option>)}
          </select>
          <Btn variant="secondary" Icon={Plus} onClick={() => setShowForm((s) => !s)}>
            {showForm ? "Close" : "Record grievance"}
          </Btn>
        </div>
      </div>

      {showForm && (
        <form onSubmit={submit} style={{ padding: 16, marginBottom: 16, border: `1px solid ${C.border}`, borderRadius: 8, background: C.bg }}>
          <FieldRow cols="1fr 1fr 1fr">
            <div>
              <Label>Community *</Label>
              <select style={field} value={form.community_id} onChange={(e) => setForm({ ...form, community_id: e.target.value })} required>
                <option value="" disabled>Select community…</option>
                {communities.map((c) => <option key={c.community_id} value={c.community_id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Channel *</Label>
              <select style={field} value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}>
                {CHANNEL_OPTIONS.map((o) => <option key={o} value={o}>{label(o)}</option>)}
              </select>
            </div>
            <div>
              <Label>Received date *</Label>
              <input type="date" style={field} value={form.received_date} onChange={(e) => setForm({ ...form, received_date: e.target.value })} required />
            </div>
          </FieldRow>
          <div style={{ marginBottom: 12 }}>
            <Label>Description</Label>
            <textarea rows={3} style={{ ...field, resize: "vertical" }} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Btn type="submit" variant="primary" Icon={Plus} disabled={busy}>
              {busy ? "Saving…" : "Record"}
            </Btn>
          </div>
        </form>
      )}

      {grievances.length === 0 ? (
        <EmptyState
          Icon={MessageSquare}
          title="No grievances recorded"
          body="Capture concerns raised by affected communities through your grievance channels to support S3-3 disclosure."
        />
      ) : (
        <DataTable columns={columns} rows={grievances} keyField="grievance_id" emptyLabel="No grievances" />
      )}
    </Card>
  );
}
